// Module: useLastTickTime
// Purpose: Subscribe to the TradingDataContext per-instrument tick cache and
//          return { lastTickTime, tickType } for a given instrument_id.
//          Output reference is gated: it only changes when (a) the first non-null
//          tick arrives, or (b) the incoming tick time has drifted more than
//          (windowDays - 1) days past the previously-emitted anchor.
//          This prevents useFetchDateRange → useChartData from re-fetching on
//          every individual tick during normal simulation forward-progress.

import { useContext, useRef, useMemo } from 'react';
import { TradingDataContext } from '../../contexts/TradingDataContext';

const NULL_RESULT = { lastTickTime: null, tickType: null };

/**
 * Returns { lastTickTime: number|null, tickType: string|null } for the given
 * resolved instrument_id.
 *
 * Output object identity is stable so long as the incoming tick remains within
 * the currently-anchored window. A new object is emitted only when:
 *   1. First non-null tick arrives (one-shot initial anchor).
 *   2. Incoming tick time > (lastEmitted + (windowDays - 1) * 86_400_000).
 *      i.e. the simulation has drifted so far that a fresh historic fetch is
 *      genuinely warranted.
 *
 * @param {string|null|undefined} instrumentId  — resolved_instrument_id
 * @param {number} [windowDays=7]               — historic fetch window in days;
 *                                                controls how far the sim must
 *                                                drift before the anchor moves.
 * @returns {{ lastTickTime: number|null, tickType: string|null }}
 */
export function useLastTickTime(instrumentId, windowDays = 7) {
  const ctx = useContext(TradingDataContext);
  if (!ctx) throw new Error('useLastTickTime must be used within a TradingDataProvider');

  const { deploymentTicksRef, versions } = ctx;

  //--> Track last-emitted epoch so we can apply the window-crossing gate
  const lastEmittedTimeRef = useRef(null);
  //--> Stable output object reference — only replaced when gate passes
  const outputRef = useRef(NULL_RESULT);

  //! Window-crossing threshold: only re-emit when the sim has drifted beyond
  //! (windowDays - 1) days from the anchored time. Ordinary per-tick forward
  //! progress stays well within this window; useLiveUpdates handles those bars.
  const windowGateMs = (windowDays - 1) * 86_400_000;

  return useMemo(() => {
    if (!instrumentId) {
      //--> Reset emitted ref so a subsequent valid id starts fresh
      lastEmittedTimeRef.current = null;
      outputRef.current = NULL_RESULT;
      return outputRef.current;
    }

    const entry = deploymentTicksRef.current.get(instrumentId);
    const rawTime = entry?.lastTickTime ?? null;
    const tickType = entry?.tickType ?? null;

    if (rawTime == null) {
      lastEmittedTimeRef.current = null;
      outputRef.current = NULL_RESULT;
      return outputRef.current;
    }

    const prev = lastEmittedTimeRef.current;

    //! Window-crossing gate: only emit a new object when the raw time has
    //! advanced beyond the window-gate threshold relative to what we last emitted.
    //! First tick (prev === null) always passes (one-shot initial anchor).
    if (prev !== null && (rawTime - prev) <= windowGateMs) {
      //--> Within window — return the existing stable reference
      return outputRef.current;
    }

    //--> Gate crossed (or first emission) — update and return new object
    lastEmittedTimeRef.current = rawTime;
    outputRef.current = { lastTickTime: rawTime, tickType };
    return outputRef.current;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  //! versions.deploymentTicks is the version counter that bumps on every tick.
  //! instrumentId in deps handles instrument-switch case (resets gate).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instrumentId, versions.deploymentTicks]);
}
